// src/components/dialog/DlgDashboardEdit.tsx

import { CancelTwoTone, DeleteTwoTone, SaveTwoTone } from '@mui/icons-material'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material'
import { useState, useEffect } from 'react'

import { DashboardId, UserId } from '@/types/domain'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import { OperatorData, useDashboardApi, useOperator } from '../../hooks'

const timeSpanData = [
  { label: 'Letzter voller Monat', value: 'lastFullMonth' },
  { label: 'Letzten 30 Tage', value: 'lastThirtyDays' },
  { label: 'Letzten 2 Monate', value: 'lastTwoMonths' },
  { label: 'Letzten 6 Monate', value: 'lastSixMonths' },
  { label: 'Dieses Jahr (Januar bis heute)', value: 'thisYear' },
  { label: 'Letztes Jahr', value: 'lastYear' },
  { label: 'Letzten 2 Jahre', value: 'lastTwoYears' },
  { label: 'Alle verfügbaren Jahre', value: 'allYears' },
]

type DlgDashboardEditProps = KARMA_BasicDialog & {
  data: {
    id: DashboardId
    granularity: string
    name: string
  }
  onChangeGranularity: (granularity: string) => void
  onChangeName: (name: string) => void
}

export const DlgDashboardEdit = ({
  data,
  onChangeGranularity,
  onChangeName,
  onClose,
  open,
}: DlgDashboardEditProps) => {
  const { id: dId, granularity: initialGranularity, name: initialName } = data

  const opData: OperatorData = useOperator()
  const user_id: UserId = opData?.id

  const { remove, update } = useDashboardApi(null)

  const [name, setName] = useState<string>(initialName ?? '')
  const [granularity, setGranularity] = useState<string>(
    initialGranularity ?? 'lastFullMonth'
  )

  const changeName = event => setName(event.target.value)
  const changeGranularity = event => setGranularity(event.target.value)

  const deleteDashboard = () => remove(dId)
  const onUpdateDashboard = () => {
    update(dId, { name, user_id })

    onChangeName(name)
    onChangeGranularity(granularity)
  }

  useEffect(() => {
    if (!open) return

    setName(initialName ?? '')
    setGranularity(initialGranularity ?? 'lastFullMonth')
  }, [open, initialName, initialGranularity])

  const NO_MOD = name === initialName && granularity === initialGranularity

  return (
    <Dialog maxWidth={'xl'} open={open}>
      <DialogTitle>
        Dashboard Einstellungen{' '}
        <Chip color={'primary'} label={`ID:${dId}`} sx={{ bgcolor: '#999' }} />
      </DialogTitle>

      <DialogContent>
        <TextField
          error={name === ''}
          fullWidth
          label={'Titel'}
          onChange={changeName}
          sx={{ mx: 0 }}
          value={name}
        />

        <TextField
          fullWidth
          label={'Zeitrahmen'}
          onChange={changeGranularity}
          select
          sx={{ mx: 0 }}
          value={granularity}
        >
          {timeSpanData.map(item => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions>
        <Button onClick={deleteDashboard}>
          <DeleteTwoTone />
          Löschen
        </Button>

        <Divider />

        <Button disabled={NO_MOD} onClick={onUpdateDashboard}>
          <SaveTwoTone />
          Aktualisieren
        </Button>

        <Button onClick={onClose}>
          <CancelTwoTone />
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
